import { useQuery } from "@apollo/client";
import { ORIGIN_COUNTRIES } from "AppSettings";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import Status from "components/base/Status";
import moment from "moment";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getOriginalFileName } from "utils/string";
import { FETCH_CERTIFICATES, PAGE_SIZE } from "./graphql";

const STATUS_FILTER = {
  ALL: "ALL",
  EXPIRED: "Expired",
  EXPIRING_SOON: "Expiring soon",
  NOT_EXPIRED: "Not expired",
};

const formatDate = (value) =>
  value ? moment(value).format("YYYY-MM-DD") : "-";

const formatCountry = (countryCode) => {
  if (!countryCode) return "-";
  const country = ORIGIN_COUNTRIES.find((item) => item.code === countryCode);
  if (!country) return countryCode;
  return `${country.emoji} ${country.name}`;
};

const getStatusDisplay = (expiryStatus) => {
  if (expiryStatus === STATUS_FILTER.EXPIRED) {
    return { emoji: "🔴", text: STATUS_FILTER.EXPIRED };
  }
  if (expiryStatus === STATUS_FILTER.EXPIRING_SOON) {
    return { emoji: "🟡", text: STATUS_FILTER.EXPIRING_SOON };
  }
  if (expiryStatus === STATUS_FILTER.NOT_EXPIRED) {
    return { emoji: "🟢", text: STATUS_FILTER.NOT_EXPIRED };
  }
  return { emoji: "⚪", text: "Unknown" };
};

const CertificatesPage = () => {
  const [showAllProducts, setShowAllProducts] = useState({});
  const [searchParams, setSearchParams] = useSearchParams({
    offset: 0,
    q: "",
    certificateType: "ALL",
    country: "",
    status: STATUS_FILTER.ALL,
  });
  const params = Object.fromEntries(searchParams.entries());
  const offset = parseInt(params.offset, 10) || 0;
  const q = params.q || "";
  const certificateType = params.certificateType || "ALL";
  const country = params.country || "";
  const status = params.status || STATUS_FILTER.ALL;

  const { loading, error, data } = useQuery(FETCH_CERTIFICATES, {
    variables: {
      offset,
      limit: PAGE_SIZE,
      q,
      certificateTypes:
        certificateType === "ALL" ? undefined : [certificateType],
      country: country || undefined,
      expiryStatus: status === STATUS_FILTER.ALL ? undefined : status,
    },
    fetchPolicy: "network-only",
  });

  const allTypes = ["DOC", "CPC", "GMARK", "CCC"];
  const certificates = data?.certificates?.results || [];

  const total = data?.certificates?.total || 0;

  return (
    <Page
      title="Certificates"
      className="h-screen"
      rightButtons={
        <div className="flex space-x-6">
          <Link to="/certificates/cpsc">CPSC Products</Link>
          <Link to="/certificates/new">+ Add Certificate</Link>
        </div>
      }
    >
      <div className="p-6 h-full flex flex-col">
        <div>
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex-1">
              <SearchBar
                initialQuery={q}
                placeholder="Search by title, number, issuer..."
                onChange={(nextQ) =>
                  setSearchParams({ ...params, q: nextQ, offset: 0 })
                }
              />
            </div>
            <Select
              value={certificateType}
              onChange={(e) =>
                setSearchParams({
                  ...params,
                  certificateType: e.target.value,
                  offset: 0,
                })
              }
            >
              <option value="ALL">All Types</option>
              {allTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
            <Select
              value={country}
              onChange={(e) =>
                setSearchParams({
                  ...params,
                  country: e.target.value,
                  offset: 0,
                })
              }
            >
              <option value="">All Countries</option>
              {ORIGIN_COUNTRIES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name} {item.emoji}
                </option>
              ))}
            </Select>
            <Select
              value={status}
              onChange={(e) =>
                setSearchParams({
                  ...params,
                  status: e.target.value,
                  offset: 0,
                })
              }
            >
              <option value={STATUS_FILTER.ALL}>All Statuses</option>
              <option value={STATUS_FILTER.EXPIRED}>Expired</option>
              <option value={STATUS_FILTER.EXPIRING_SOON}>Expiring soon</option>
              <option value={STATUS_FILTER.NOT_EXPIRED}>Not expired</option>
            </Select>
          </div>
        </div>

        <div className="card p-0 flex-1 overflow-auto flex flex-col mt-6">
          <div className="flex-1 overflow-auto relative">
            {loading ? (
              <Spinner />
            ) : error ? (
              <Errors error={error} />
            ) : (
              <div>
                {certificates.length === 0 ? (
                  <div className="p-8 text-sm text-gray-500">
                    No certificates found.
                  </div>
                ) : (
                  certificates.map((certificate) => {
                    const statusView = getStatusDisplay(
                      certificate.expiryStatus,
                    );
                    return (
                      <div
                        key={certificate.id}
                        className="border-b border-gray-100 dark:border-gray-700 p-4 md:p-6"
                      >
                        <div className="flex flex-col gap-3">
                          <div>
                            <div className="text-xs opacity-60">
                              #{certificate.certificateNumber || "-"}
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                              <h5>
                                <Link
                                  to={`/certificates/${certificate.id}?${searchParams.toString()}`}
                                >
                                  {certificate.title || "-"} (
                                  {certificate.certificateType || "N/A"})
                                </Link>
                              </h5>
                              {!certificate.isActive ? (
                                <Status status="INACTIVE" />
                              ) : null}
                            </div>
                          </div>

                          <div className="text-sm flex flex-wrap gap-x-6 gap-y-1">
                            <div>
                              Country: {formatCountry(certificate.country)}
                            </div>
                            <div>Issuer: {certificate.issuer || "-"}</div>
                            <div>
                              Issued Date: 📅{" "}
                              {formatDate(certificate.issuedDate)}
                            </div>
                            <div>
                              Expire Date: {statusView.emoji}{" "}
                              {formatDate(certificate.expiryDate)}
                            </div>
                          </div>

                          {certificate.fileUrls?.length > 0 ? (
                            <div className="text-sm">
                              <div className="flex flex-col items-start gap-1 text-xs">
                                {certificate.fileUrls.map((fileUrl) => (
                                  <a
                                    key={fileUrl}
                                    className="inline"
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    📎 {getOriginalFileName(fileUrl)}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ) : null}

                          <div className="opacity-80">
                            <div className="flex items-center gap-2">
                              <label>
                                Products ({(certificate.products || []).length}
                                ):{" "}
                              </label>
                              {(certificate.products || []).length > 5 ? (
                                <button
                                  type="button"
                                  className="text-xs text-sky-600 hover:text-sky-700"
                                  onClick={() =>
                                    setShowAllProducts((prev) => ({
                                      ...prev,
                                      [certificate.id]: !prev[certificate.id],
                                    }))
                                  }
                                >
                                  {showAllProducts[certificate.id]
                                    ? "Show less"
                                    : "Show all products"}
                                </button>
                              ) : null}
                            </div>
                            {(certificate.products || []).length > 0 ? (
                              <div className="pt-2">
                                {(showAllProducts[certificate.id]
                                  ? certificate.products || []
                                  : (certificate.products || []).slice(0, 5)
                                ).map((product) => (
                                  <div
                                    key={product.id}
                                    className="border-t border-gray-100 py-2"
                                  >
                                    <div>
                                      [{product.number}] {product.name}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="pt-2">-</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {data ? (
            <div className="p-4 md:p-6 flex justify-end items-center space-x-2 opacity-70">
              {offset > 0 ? (
                <Button
                  prev
                  onClick={() =>
                    setSearchParams({
                      ...params,
                      offset: Math.max(0, offset - PAGE_SIZE),
                    })
                  }
                />
              ) : null}
              <div>
                {total === 0 ? 0 : offset + 1}-
                {Math.min(PAGE_SIZE + offset, total)} / {total}
              </div>
              {total > offset + PAGE_SIZE ? (
                <Button
                  next
                  onClick={() =>
                    setSearchParams({ ...params, offset: PAGE_SIZE + offset })
                  }
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Page>
  );
};

export default CertificatesPage;
